/*
 * "Offer" part of state tree is maintained by offer-redux.  Anything else offer related that isn't
 * handled by offer-redux (i.e. it isn't related to penciling) should go here.
 */

// externals
import type { Reducer } from 'redux';
import type { Draft } from 'immer';
import { produce } from 'immer';

// libraries
import { setCustomAttribute } from '@makemydeal/dr-activities-common';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { OfferInfoState, PendingSaveAction } from '@makemydeal/dr-dash-types';
import { SEARCH_AND_MERGE_BLOCKED_MESSAGE } from '@makemydeal/dr-dash-types';
import type { BaggageEntry } from '@makemydeal/dr-dash-bff-types';
import type { SocketChangedBy } from '@makemydeal/dr-shared-bff-types';
import { bootstrapActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';

// consts/enums
import {
    CLEAR_SOCKET_EVENT_NOTIFICATION,
    COBUYER_SHOW_INVALID_MODAL,
    MARK_GTL_BANNER_SHOWN_ON_LOAD,
    SET_PENDING_REVIEW_CONTEXT,
    SET_PENDING_REVIEW_RESOLUTION_PATH,
    MARK_PENDING_REVIEW_HANDLED,
    SET_PENDING_REVIEW_TARGET_VERSION,
    SET_PENDING_REVIEW_RESOLUTION,
    CLEAR_PENDING_REVIEW,
    RESOLVE_PENDING_REVIEW_REQUESTED,
    LINKS_FAILURE,
    LINKS_SUCCESS,
    MAKE_CURRENT,
    MAKE_CURRENT_COMPLETED,
    OFFER_SAVE_FAILURE,
    OFFER_SAVE_NOCHANGE_REQUEST,
    OFFER_SAVE_NOCHANGE_SUCCESS,
    OFFER_SAVE_REQUEST,
    OFFER_SAVE_SUCCESS,
    OFFER_SUBMISSION_FAILURE,
    OFFER_SUBMISSION_REQUEST,
    OFFER_SUBMISSION_SUCCESS,
    SAVE_OFFER,
    SAVE_OFFER_WITH_CANCEL_PENDING,
    SAVE_OFFER_AFTER_CANCEL,
    SET_CREDIT_RATING_DISPLAY_TYPE,
    SET_SHOW_SOFT_PULL_CONSENT_MODAL,
    SET_SOFT_PULL_CONSENT_PROVIDED,
    SOFT_SAVE_FAILURE,
    SOFT_SAVE_SUCCESS,
    SUBMIT_OFFER,
    UPDATE_DEAL_REF_ID
} from '../actionTypes/offerActionTypes';
import { OFFER_SENT_WITH_TEST_DRIVE, TEST_DRIVE_READY_TO_SEND } from '../actionTypes/testDriveActionTypes';
import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { SCENARIOS_FETCH_FAILURE } from '../actionTypes/multiScenarioActionTypes';
import { SOCKET_EVENT_IGNORED, SOCKET_EVENT_RECEIVED } from '../actionTypes/socketActionTypes';
import { REQUEST_CANCEL_ORDER_FAILURE } from '../types/orders';
import { CREDIT_APP_SEARCH_MERGE_DEAL } from '../actionTypes/creditAppSearchActionTypes';
import { SHOPPER_URLS_REQUEST, SHOPPER_URLS_SUCCESS, SHOPPER_URLS_FAILURE } from '../actionTypes/shopperUrlsActionTypes';

// utils
import {
    initDraftSavedSuccessState,
    shouldInitDraftSavedState,
    storeLinksInState,
    updateOfferSaveFailureState,
    updateOfferSavedSuccessState
} from '../utils/saveOfferStateUtils';
import { generateFallbackUrls } from '../utils/generateFallbackUrls';

const { INIT_SUCCESS, INIT_PENCIL_SUCCESS } = initActionTypes;
const { BOOTSTRAP_REQUEST, BOOTSTRAP_FAILURE } = bootstrapActionTypes;

const DC_SOFT_PULL_CONSENT_BAGGAGE_NAME = 'dc.consent.creditPull';

const getSoftPullConsentFromBaggage = (baggage: BaggageEntry[] | undefined): boolean | undefined => {
    if (!Array.isArray(baggage)) {
        return undefined;
    }
    const softPullConsentBaggage = baggage.find(({ name }: BaggageEntry) => name === DC_SOFT_PULL_CONSENT_BAGGAGE_NAME);
    if (softPullConsentBaggage?.value?.content?.consentGiven !== undefined) {
        return Boolean(softPullConsentBaggage.value.content.consentGiven);
    }
    return undefined;
};

export type OfferInfoReducer = Reducer<OfferInfoState>;

export const reducer: OfferInfoReducer = (state = {} as OfferInfoState, action: AnyFSA) => {
    if (!action) {
        return state;
    }

    return produce(state, (draft: Draft<OfferInfoState>) => {
        switch (action.type) {
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS:
            case INIT_SUCCESS:
            case OFFER_SUBMISSION_SUCCESS: {
                const offer = action.payload.offer || {};
                draft.offerId = offer.id || offer.offerId;
                draft.hasBeenSent = offer.hasBeenSent || false;
                draft.isOfferBeingSubmitted = false;
                draft.dealXgVersion = offer.dealExchangeVersionId;

                const softPullConsent = getSoftPullConsentFromBaggage(action.payload.baggage);
                if (softPullConsent !== undefined) {
                    draft.softPullConsentProvided = softPullConsent;
                }

                if (action.type === bootstrapActionTypes.BOOTSTRAP_SUCCESS) {
                    draft.isVersionSpecificLoad = Boolean(action.meta?.isVersionSpecificLoad);
                    // The credit Rating/Tier radio choice is a per-load UI preference with no server-side
                    // source of truth, so it cannot self-correct from the payload the way
                    // softPullConsentProvided does. offerInfo is a global slice that is never reset, and
                    // BOOTSTRAP_SUCCESS is re-dispatched into the live store on a deal-version switch
                    // (makeCurrentDeal), so clear it here to keep a choice made on one version from
                    // carrying into the next load; the toggle then re-derives from that deal's credit score.
                    draft.creditRatingDisplayType = undefined;
                    if (shouldInitDraftSavedState(action)) {
                        initDraftSavedSuccessState(draft, action);
                    }
                }

                return;
            }
            case OFFER_SUBMISSION_FAILURE: {
                draft.hasBeenSent = false;
                draft.isOfferBeingSubmitted = false;
                return;
            }
            case OFFER_SUBMISSION_REQUEST:
            case SUBMIT_OFFER: {
                draft.isOfferBeingSubmitted = true;
                return;
            }
            case TEST_DRIVE_READY_TO_SEND: {
                if (!state.hasBeenSent) {
                    draft.isTestDriveReadyBeforeOffer = true;
                    return;
                }
                return;
            }
            case OFFER_SENT_WITH_TEST_DRIVE: {
                draft.isTestDriveReadyBeforeOffer = false;
                return;
            }
            case SAVE_OFFER:
            case OFFER_SAVE_NOCHANGE_REQUEST:
            case OFFER_SAVE_REQUEST: {
                if (action.meta.isDraft) {
                    draft.isDraftBeingSaved = true;
                    draft.hasDraftBeenSaved = false;
                } else {
                    draft.isOfferBeingSaved = true;
                    draft.hasBeenSaved = false;
                }
                return;
            }

            case UPDATE_DEAL_REF_ID: {
                draft.fsDealId = action.payload.dealRefId;
                draft.dealRefId = action.payload.dealRefId;
                return;
            }

            case OFFER_SAVE_SUCCESS: {
                draft.dealXgVersion = action.payload.dealXgVersion;

                if (!action.meta.isDraft) {
                    draft.mergedDeal = undefined;
                }
                setCustomAttribute('dealXgVersion', action.payload.dealXgVersion);
                updateOfferSavedSuccessState(draft, action);

                if (action.payload.createdDate) {
                    draft.dealCreatedDate = action.payload.createdDate;
                }

                // Only store links if they exist in the payload
                // With deferred URL generation, URLs won't be in the save response
                // They're generated separately via SHOPPER_URLS_SUCCESS, so we don't want to overwrite them
                if (action.payload.creditAppUrl || action.payload.shopperPageUrl) {
                    storeLinksInState(draft, action.payload);
                }

                const softPullConsent = getSoftPullConsentFromBaggage(action.payload.baggage);
                if (softPullConsent !== undefined) {
                    draft.softPullConsentProvided = softPullConsent;
                }

                return;
            }
            case SOFT_SAVE_SUCCESS:
            case LINKS_SUCCESS: {
                updateOfferSavedSuccessState(draft);
                storeLinksInState(draft, action.payload);
                return;
            }
            case OFFER_SAVE_NOCHANGE_SUCCESS: {
                return updateOfferSavedSuccessState(draft);
            }
            case OFFER_SAVE_FAILURE: {
                if (action.payload?.response?.message === SEARCH_AND_MERGE_BLOCKED_MESSAGE) {
                    draft.searchAndMergeCleanedUp = true;
                }
                return updateOfferSaveFailureState(draft, action);
            }
            case SOFT_SAVE_FAILURE:
            case LINKS_FAILURE: {
                return updateOfferSaveFailureState(draft);
            }
            case SOCKET_EVENT_IGNORED: {
                const dealRefId = action.payload.data.coxAutoReferences?.dealRefId;
                if (dealRefId) {
                    draft.dealRefId = dealRefId;
                    draft.fsDealId = dealRefId;
                }

                break;
            }
            case SOCKET_EVENT_RECEIVED: {
                const dealRefId = action.payload.data.coxAutoReferences?.dealRefId;
                if (dealRefId) {
                    draft.dealRefId = dealRefId;
                    draft.fsDealId = dealRefId;
                }
                const changedBy: SocketChangedBy | undefined = action.payload.data.changedBy;
                if (changedBy?.persona?.toLowerCase() === 'system' && !action.payload.isCommonConsumerSourced) return;
                const socketPaymentStatus =
                    action.payload.data.dealStructure?.financialDetails?.calculationDetails?.payment?.calculationState
                        ?.paymentStatus;
                draft.socketEventNotification = {
                    updatedMilestones: action.payload.updatedMilestones,
                    changedBy: action.payload.data.changedBy,
                    dealProgressMilestones: action.payload.data.dealProgressMilestones,
                    message: action.payload.data.message,
                    dealStructure: {
                        versionCreatedBy: {
                            applicationName: action.payload.data.dealStructure?.versionCreatedBy?.applicationName
                        },
                        versionComment: action.payload.data.dealStructure?.versionComment,
                        acceptedByBuyer: action.payload.data.dealStructure?.acceptedByBuyer,
                        ...(socketPaymentStatus !== undefined && {
                            financialDetails: {
                                calculationDetails: { payment: { calculationState: { paymentStatus: socketPaymentStatus } } }
                            }
                        })
                    }
                };
                return;
            }
            case CLEAR_SOCKET_EVENT_NOTIFICATION:
            case BOOTSTRAP_REQUEST: {
                draft.socketEventNotification = undefined;
                return;
            }
            case MARK_GTL_BANNER_SHOWN_ON_LOAD: {
                draft.gtlBannerShownOnLoad = true;
                return;
            }
            case SET_PENDING_REVIEW_CONTEXT: {
                // Sales Check-In reuses version identifiers across publishes, so this cannot be
                // conditioned on the version having changed. See setPendingReviewContext.
                draft.pendingReviewResolution = undefined;
                draft.pendingReview = action.payload;
                return;
            }
            case SET_PENDING_REVIEW_RESOLUTION_PATH: {
                if (draft.pendingReview) {
                    draft.pendingReview.resolutionPath = action.payload;
                }
                return;
            }
            case SET_PENDING_REVIEW_TARGET_VERSION: {
                if (draft.pendingReview) {
                    draft.pendingReview.targetDealXgVersion = action.payload;
                }
                return;
            }
            case MARK_PENDING_REVIEW_HANDLED: {
                draft.pendingReviewHandledVersion = action.payload;
                return;
            }
            case SET_PENDING_REVIEW_RESOLUTION: {
                draft.pendingReviewResolution = action.payload;
                return;
            }
            case RESOLVE_PENDING_REVIEW_REQUESTED: {
                if (draft.pendingReview) {
                    draft.pendingReview.isResolving = true;
                }
                return;
            }
            case PAYMENT_RECEIVED: {
                const offerType = action.meta?.offerType;
                if (action.meta?.isStalePaymentResponse || !offerType || action.payload?.[offerType]?.success !== true) {
                    return;
                }
                draft.hasSuccessfulPaymentSinceInit = true;
                return;
            }
            case INIT_PENCIL_SUCCESS: {
                // Scenario hydration dispatches this per non-primary scope via createScopedAction, and a
                // global reducer sees every one of them — see multiScenarioActionCreators DE749297. Only
                // the unscoped bootstrap dispatch means "a version is loading".
                if (action.meta?.__scopeMeta?.targetScope) {
                    return;
                }
                draft.hasSuccessfulPaymentSinceInit = false;
                return;
            }
            case CLEAR_PENDING_REVIEW: {
                draft.pendingReview = undefined;
                draft.pendingReviewHandledVersion = undefined;
                draft.pendingReviewResolution = undefined;
                return;
            }
            case COBUYER_SHOW_INVALID_MODAL: {
                draft.showInvalidCoBuyerModal = action.payload ?? false;
                return;
            }
            case MAKE_CURRENT: {
                draft.isMakingCurrent = true;
                return;
            }
            case BOOTSTRAP_FAILURE:
            case MAKE_CURRENT_COMPLETED:
            case SCENARIOS_FETCH_FAILURE: {
                draft.isMakingCurrent = false;
                return;
            }
            case SAVE_OFFER_WITH_CANCEL_PENDING: {
                draft.saveWithCancelPending = true;
                draft.pendingSaveAction = action.payload as PendingSaveAction;
                return;
            }
            case SAVE_OFFER_AFTER_CANCEL: {
                draft.saveWithCancelPending = false;
                draft.pendingSaveAction = undefined;
                draft.fsDealId = '';
                draft.dealRefId = '';
                return;
            }
            case REQUEST_CANCEL_ORDER_FAILURE: {
                // Clear pending save state if cancellation fails
                draft.saveWithCancelPending = false;
                draft.pendingSaveAction = undefined;
                return;
            }
            case CREDIT_APP_SEARCH_MERGE_DEAL: {
                const dealRefId = action.payload.coxAutoReferences?.dealRefId;
                const dealRefIdPartner = action.payload.coxAutoReferences?.dealRefIdPartner;
                if (dealRefId) {
                    draft.dealRefId = dealRefId;
                    draft.fsDealId = dealRefId;
                }
                draft.mergedDeal = {
                    mergeSourceDealXgId: action.payload.dealId,
                    dealRefIdPartner,
                    creditApplication: action.payload.dealStructure?.creditApplication,
                    previousDealRefId: action.meta.previousDealRefId
                };
                return;
            }
            case SET_SOFT_PULL_CONSENT_PROVIDED: {
                draft.softPullConsentProvided = action.payload;
                return;
            }
            case SET_CREDIT_RATING_DISPLAY_TYPE: {
                draft.creditRatingDisplayType = action.payload;
                return;
            }
            case SET_SHOW_SOFT_PULL_CONSENT_MODAL: {
                draft.showSoftPullConsentModal = action.payload;
                return;
            }
            case SHOPPER_URLS_REQUEST: {
                draft.urlsLoading = true;
                draft.urlsError = null;
                return;
            }
            case SHOPPER_URLS_SUCCESS: {
                draft.urlsLoading = false;
                draft.urlsError = null;
                storeLinksInState(draft, action.payload);
                return;
            }
            case SHOPPER_URLS_FAILURE: {
                draft.urlsLoading = false;
                draft.urlsError = action.payload;

                // Generate fallback URLs so users can still share with customers
                const dealExchangeId = action.meta?.dealExchangeId;
                if (dealExchangeId) {
                    const fallbackUrls = generateFallbackUrls(dealExchangeId);
                    storeLinksInState(draft, fallbackUrls);
                }

                return;
            }
        }
    });
};
